<i18n src="./resources/contactTypes.json"></i18n>

<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="surface lighten-1 ecs-contact-list-header">
      <div>
        {{ header }}
        <v-progress-circular v-if="loading" size="16" width="2" class="loading" color="primary" indeterminate />
      </div>
      <template v-slot:actions>
        <v-icon color="primary">mdi-menu-down</v-icon>
      </template>
    </v-expansion-panel-header>
    <v-expansion-panel-content ref="listContent" class="surface ecs-contact-list-content">
      <div class="slide-card" :dark="$vuetify.theme.dark">
        <div class="ecs-contact-list wrapper">
          <div v-if="contactSelection" class="scroller custom-scrollbar">
            <ecs-generic-no-results-message-box v-if="items.length === 0 && search" :search="search" absolute />
            <v-virtual-scroll
              ref="itemsContainer"
              class="scroller custom-scrollbar"
              :items="items"
              :item-height="60"
              :height="scrollerHeight.height"
              @scroll="detectPosition"
            >
              <template v-slot="{ item }">
                <div
                  class="contact-item"
                  @mouseenter.stop="openQuickContactCard($event, item)"
                  @mouseleave.stop="closeQuickContactCard"
                >
                  <EcsAvatar :contact="item" :alt="item.name" :size="40" class="avatar" :stateful="true" />
                  <div class="contact-item-body">
                    {{ item.name }}
                  </div>
                  <div class="contact-item-action">
                    <span :class="`count n-${(item.phones || []).length + (item.mobiles || []).length || '0'}`">
                      {{ (item.phones || []).length + (item.mobiles || []).length }}
                    </span>
                    <v-btn icon color="primary" @click="selection(item, $event)">
                      <v-icon>mdi-send</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
            </v-virtual-scroll>
          </div>
          <div v-else-if="dragEnabled" class="scroller custom-scrollbar" :style="scrollerHeight">
            <Container
              ref="draggableContainer"
              :get-ghost-parent="getGhostParent"
              lock-axis="y"
              drag-handle-selector=".item-drag-handle"
              drop-class="ecs-contact-item-ghost-drop"
              drag-class="ecs-contact-item-ghost"
              :drop-placeholder="dropPlaceholderOptions"
              @drop="onDrop"
            >
              <Draggable v-for="item in sortedItems" :key="item.id">
                <div
                  class="contact-item item-drag-handle"
                  @mouseenter.stop="openQuickContactCard($event, item)"
                  @mouseleave.stop="closeQuickContactCard"
                >
                  <EcsAvatar :contact="item" :alt="item.name" :size="40" class="avatar" :stateful="true" />
                  <div
                    class="contact-item-body"
                    :class="{ details: showContactType }"
                    @click.stop="openContactCard(item)"
                  >
                    <div class="main-text">
                      {{ item.name }}
                    </div>
                    <div v-if="showContactType" class="details-text">
                      {{ $t(item.contactType) }}
                    </div>
                  </div>
                </div>
              </Draggable>
            </Container>
          </div>
          <div v-else class="scroller custom-scrollbar">
            <ecs-generic-no-results-message-box v-if="items.length === 0 && search" :search="search" absolute />
            <v-virtual-scroll
              ref="itemsContainer"
              class="scroller custom-scrollbar"
              :items="items"
              :item-height="60"
              :height="scrollerHeight.height"
              @scroll="detectPosition"
            >
              <template v-slot="{ item }">
                <div
                  class="contact-item"
                  @mouseenter.stop="openQuickContactCard($event, item)"
                  @mouseleave.stop="closeQuickContactCard"
                >
                  <EcsAvatar :contact="item" :size="40" class="avatar" :stateful="true" />
                  <div class="contact-item-body" @click.stop="openContactCard(item)">
                    {{ item.name }}
                  </div>
                </div>
              </template>
            </v-virtual-scroll>
          </div>
        </div>
      </div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { debounce } from 'lodash';
import { Container, Draggable } from 'vue-smooth-dnd';
import EcsGenericNoResultsMessageBox from '@/components/ecs-generic-no-results-message-box/EcsGenericNoResultsMessageBox';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar.vue';

export default {
  name: 'EcsContactsList',

  components: { Draggable, Container, EcsAvatar, EcsGenericNoResultsMessageBox },

  props: {
    header: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default() {
        return [];
      }
    },
    search: {
      type: String,
      default: ''
    },
    loadStatesOnScroll: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: Number,
      default() {
        return this.$el.parent.height();
      }
    },
    showContactType: {
      type: Boolean,
      default: false
    },
    dragEnabled: {
      type: Boolean,
      default: false
    },
    // Boolean to Enable the list to be a selection list.
    contactSelection: {
      type: Boolean,
      default: false
    },
    // Boolean to Enable single selection when the list is a selection list.
    useSingleSelection: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      sortedItems: this.items
    };
  },

  computed: {
    scrollerHeight() {
      return {
        height: `${this.maxHeight}px`
      };
    },

    dropPlaceholderOptions() {
      return {
        className: 'ecs-contact-item-placeholder',
        animationDuration: '100',
        showOnTop: true
      };
    }
  },

  watch: {
    items(newValue) {
      this.sortedItems = newValue;
    }
  },

  methods: {
    openContactCard(contact) {
      if (this.contactSelection) {
        return;
      }

      this.closeQuickContactCard();
      this.$root.$emit('open-contact-card-actions', { contact });
    },

    emitErrorMessage(message) {
      this.$emit('emit-error-message', message);
    },

    /**
     * This method will emit the selected contact to the upper components or it will display a telephony selection card.
     * @param contact {Object} - The contact that was selected
     * @param event {Object} - The event that was triggered
     */
    selection(contact, event) {
      if (!this.contactSelection) {
        return;
      }

      if (contact.phones.length + contact.mobiles.length > 1) {
        this.showContactPhoneSelector(contact, event);
      } else {
        this.$emit('contacts-selection', contact);
      }
    },

    showContactPhoneSelector(contact, event) {
      this.$root.$emit('open-contact-phone-selector-card', {
        contact,
        x: event.target.getBoundingClientRect().right,
        y: event.target.getBoundingClientRect().top,
        action: phone => {
          this.$emit('contacts-selection', {
            id: contact.id,
            name: contact.name,
            endPoint: phone
          });
        }
      });
    },

    openQuickContactCard(event, contact) {
      if (this.contactSelection) {
        return;
      }

      const cardWidth = 320;
      this.$root.$emit('open-contact-card', {
        contact,
        x: event.target.getBoundingClientRect().left - cardWidth,
        y: event.target.getBoundingClientRect().top
      });
    },

    closeQuickContactCard() {
      this.$root.$emit('close-contact-card');
    },

    loadMoreContacts: debounce(function() {
      if (!this.loading) {
        this.$emit('load-more-data');
      }
    }, 500),

    detectPosition(event) {
      const loadMoreGap = 100;
      const element = event.target;

      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < loadMoreGap) {
        this.loadMoreContacts();
      }
    },

    restartScroll() {
      if (this.$refs.itemsContainer) {
        this.$refs.itemsContainer.$el.scrollTop = 0;
      }
    },

    getGhostParent() {
      return document.getElementById('contacts_directory_container');
    },

    onDrop(dropResult) {
      const { removedIndex, addedIndex, payload } = dropResult;
      if ((!removedIndex && !addedIndex) || removedIndex === addedIndex) {
        return;
      }

      const sortedItems = [...this.sortedItems];
      let itemToAdd = payload;

      if (removedIndex !== null) {
        itemToAdd = sortedItems.splice(removedIndex, 1)[0];
      }

      if (addedIndex !== null) {
        sortedItems.splice(addedIndex, 0, itemToAdd);
      }

      this.sortedItems = sortedItems;
      this.$emit('contacts-updated', this.sortedItems);
    }
  }
};
</script>

<style scoped src="./scoped.css" />

<style>
.v-expansion-panels {
  border-radius: unset;
}
.v-expansion-panel-content__wrap {
  padding: 0;
}
</style>

<style scoped>
.loading {
  margin: -4px 0 0 8px;
}

.v-expansion-panel::before {
  box-shadow: none;
}

.wrapper {
  overflow: auto;
}

.ecs-contact-list-header {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  text-align: left;
}

.ecs-list-header {
  height: 60px;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 0.14em;
  text-align: left;
  color: var(--v-primary-base) !important;
}

.ecs-contact-list {
  position: relative;
  background: transparent !important;
  overflow: hidden;
  padding: 0;
}

.contact-length-container {
  display: block !important;
}

.item-length {
  position: absolute;
  right: 60px;
  top: 14px;
  border-radius: 12px;
  padding: 4px 8px;
}

.v-expansion-panel--active .v-expansion-panel-header {
  min-height: inherit;
}

.v-expansion-panel--active:not(:first-child),
.v-expansion-panel--active + .v-expansion-panel {
  margin: 0;
}

.v-expansion-panel--active:not(:first-child)::after,
.v-expansion-panel--active + .v-expansion-panel::after {
  opacity: 1;
}

.v-expansion-panel::before {
  box-shadow: none;
}

.v-expansion-panels .v-expansion-panel::after {
  border-color: var(--v-border-color);
}

.data-loader {
  height: 60px;
  width: 100% !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contact-item {
  display: grid;
  grid-template-columns: 60px auto fit-content(48px);
  height: 60px;
  line-height: 60px;
  color: #444;
  background-color: transparent;
  transition: background-color 0.2s ease;
  cursor: pointer;
  user-select: none;

  /* This will enable the GPU acceleration for the element. */
  will-change: transform;
  -webkit-transform: translateZ(0);
  -moz-transform: translateZ(0);
  -ms-transform: translateZ(0);
  -o-transform: translateZ(0);
  transform: translateZ(0);
}

[dark] .contact-item {
  color: #fff;
}

.contact-item.section-title {
  grid-template-columns: auto;
  text-transform: uppercase;
  color: var(--v-primary-base);
  padding: 0 12px;
}

.contact-item-body {
  padding: 0 4px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.contact-item-body.details {
  padding-top: 12px;
  line-height: 18px;
}

.contact-item-body.details .main-text {
}

[dark] .contact-item-body.details .details-text {
  color: var(--v-surface-lighten4);
}

.contact-item-body.details .details-text {
  color: #666;
  font-size: 12px;
}

.contact-item-action {
  white-space: nowrap;
}

.contact-item-action .v-btn {
  margin-right: 12px;
}

.contact-item:hover {
  background-color: #0002;
}

.contact-item .avatar {
  margin: 8px;
}

.count {
  padding: 0 6px;
  border-radius: 20px;
  display: inline-block;
  background-color: #0003;
  height: 22px;
  line-height: 20px;
  margin-right: 4px;
  border: 1px solid #fff4;
}

.count.n-0,
.count.n-1 {
  display: none;
}

.action-btn {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
}

.action-btn .v-icon {
  margin-left: 4px;
}

.ecs-contact-item-ghost {
  transition: all 0.1s ease;
  box-shadow: 0 0 10px #0004;
  cursor: grabbing;
  background-color: var(--v-surface-lighten1);
  color: inherit;
}

.ecs-contact-item-ghost-drop {
  transition: all 0.1s ease-out;
  opacity: 1;
}

.item-drag-handle {
  cursor: move;
}
</style>
